/**
 * Reconciliation Intelligence Workspace — Sprint 3A.
 *
 * The "operational truth engine" for the day. Per-product cards show
 * book vs physical, gain/loss, variance % and 🟢🟡🔴 status derived
 * from configurable thresholds (Threshold Administration).
 *
 * PO directive 2026-08-01: **never auto-complete.** Even when every
 * product is 🟢, the operator must explicitly acknowledge before the
 * workflow step flips to COMPLETE. When variance is 🟡 or 🔴,
 * investigation notes are required.
 *
 * STD-009 compliant: loading states, toasts on save/complete,
 * explicit "next action" copy at every branch.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle,
  Warning,
  XCircle,
  ArrowClockwise,
  Info,
  ArrowLeft,
  ArrowRight,
  ListMagnifyingGlass,
  PencilSimple,
  ArrowCounterClockwise,
  X,
} from "@phosphor-icons/react";

import { api } from "@/lib/api";

// Weekend-aware default: Monday → previous Friday; Tue–Fri → yesterday;
// Sat/Sun → previous Friday. Encodes the "we're closed on the weekend"
// pattern the operator described (PO clarification 2026-08-10).
//
// Sprint 10 · 2026-08-21 · P0 timezone-safe date formatting (PO auth).
// The previous implementation used `.toISOString().slice(0, 10)`, which
// serialises `d` in UTC — so a Toronto operator at 23:59 local produces
// tomorrow's YYYY-MM-DD as `period_start`, silently sliding the window
// off the intended operating day. Build the ISO date directly from the
// local calendar components (getFullYear / getMonth / getDate) so
// `period_start` is anchored to the *browser's* local calendar day —
// which for Coolman operators is always America/Toronto.
function _defaultPeriodStart() {
  const d = new Date();
  const dow = d.getDay(); // 0 = Sun ... 6 = Sat
  let back = 1;
  if (dow === 1) back = 3;       // Monday → Friday
  else if (dow === 0) back = 2;  // Sunday → Friday
  else if (dow === 6) back = 1;  // Saturday → Friday
  d.setDate(d.getDate() - back);
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// ---------------------------------------------------------------------------
// Status → visual mapping
// ---------------------------------------------------------------------------
const STATUS_META = {
  GREEN:   { label: "Within tolerance", tone: "green",   icon: CheckCircle, emoji: "🟢" },
  YELLOW:  { label: "Investigate",      tone: "amber",   icon: Warning,     emoji: "🟡" },
  RED:     { label: "Escalate",         tone: "red",     icon: XCircle,     emoji: "🔴" },
  UNKNOWN: { label: "Missing input",    tone: "slate",   icon: Info,        emoji: "⚪" },
};

const TONE_CLASSES = {
  green: { pill: "bg-emerald-100 text-emerald-800 border-emerald-300",
           card: "border-emerald-200 bg-emerald-50/40",
           bar:  "bg-emerald-600" },
  amber: { pill: "bg-amber-100 text-amber-900 border-amber-300",
           card: "border-amber-200 bg-amber-50/40",
           bar:  "bg-amber-600" },
  red:   { pill: "bg-rose-100 text-rose-900 border-rose-300",
           card: "border-rose-200 bg-rose-50/40",
           bar:  "bg-rose-600" },
  slate: { pill: "bg-slate-100 text-slate-700 border-slate-300",
           card: "border-slate-200 bg-slate-50/40",
           bar:  "bg-slate-500" },
};

const fmtLitres = (v) => {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })} L`;
};

const fmtGainLoss = (v) => {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "string" ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return "—";
  const abs = Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${abs} L`;
};

const fmtPct = (v) => {
  if (v === null || v === undefined) return "—";
  return `${Number(v).toFixed(2)}%`;
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function Reconciliation() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [notes, setNotes] = useState("");
  // 2026-08-04 · Exception-based review.
  const [investigating, setInvestigating] = useState({});
  // Sprint 10 · 2026-08-06 · Deliveries now auto-source from ARC-017
  // approved receipts. Operator overrides require a reason. `overrides`
  // holds the *editing* form state per product (only while the operator
  // has the override editor open); actual persistence goes through
  // `saveOverride` → PATCH.
  const [sourceDrawer, setSourceDrawer] = useState(null);         // {product_code} | null
  const [overrideEditor, setOverrideEditor] = useState(null);     // {product_code, value, reason} | null

  // Sprint 10 · 2026-08-10 — operating period (weekend / holiday aware).
  // Default: prior weekday (Mon defaults to Fri; Tue–Fri defaults to
  // previous day; Sat/Sun defaults to Fri). Operator can override.
  const [periodStart, setPeriodStart] = useState(() => _defaultPeriodStart());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = periodStart ? { period_start: periodStart } : {};
      const { data: d } = await api.get("/reconciliation/today", { params });
      setData(d);
      if (d.completion?.investigation_notes) setNotes(d.completion.investigation_notes);
    } catch (e) {
      toast.error("Could not load reconciliation.");
    } finally {
      setLoading(false);
    }
  }, [periodStart]);

  useEffect(() => { load(); }, [load]);

  const saveOverride = async (product_code, value, reason) => {
    setSaving(true);
    try {
      const { data: d } = await api.patch("/reconciliation/today/deliveries", {
        [product_code]: { value: String(value).trim(), reason: reason.trim() },
      });
      setData(d);
      setOverrideEditor(null);
      toast.success(`Override recorded for ${product_code} · reconciliation recomputed.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save override.");
    } finally {
      setSaving(false);
    }
  };

  const revertToAuto = async (product_code) => {
    if (!window.confirm("Revert this product to the auto-calculated value?\n\nThe override history is preserved for audit.")) return;
    setSaving(true);
    try {
      const { data: d } = await api.patch("/reconciliation/today/deliveries", {
        [product_code]: null,
      });
      setData(d);
      toast.success(`Reverted ${product_code} to auto value.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not revert.");
    } finally {
      setSaving(false);
    }
  };

  // Sprint 10 · 2026-08-12 · Return-to-Storage patch. Called on blur
  // from the inline row inputs and the Movements panel inputs.
  const saveRts = async (product_code, value, note) => {
    setSaving(true);
    try {
      const clean = String(value ?? "").trim();
      const body = clean === ""
        ? { [product_code]: null }
        : { [product_code]: { value: clean, note: (note || "").trim() || null } };
      const { data: d } = await api.patch("/reconciliation/today/rts", body);
      setData(d);
      toast.success(clean === ""
        ? `RTS cleared for ${product_code}.`
        : `RTS ${clean} L recorded for ${product_code}.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save RTS.");
    } finally {
      setSaving(false);
    }
  };

  const complete = async () => {
    if (!data) return;
    if (data.has_missing_inputs) {
      toast.error("Complete Daily Readings and Tank Inventory first.");
      return;
    }
    if (data.overall_status !== "GREEN" && !notes.trim()) {
      toast.error("Investigation notes are required when status is not GREEN.");
      return;
    }
    setCompleting(true);
    try {
      const { data: d } = await api.post("/reconciliation/today/complete", {
        acknowledgement: true,
        investigation_notes: notes.trim() || null,
      });
      setData(d);
      toast.success(`Reconciliation approved. Overall status: ${d.overall_status}.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not complete reconciliation.");
    } finally {
      setCompleting(false);
    }
  };

  const completed = !!data?.completion;
  const overall = data?.overall_status || "UNKNOWN";
  const exceptions = (data?.items || []).filter((it) => it.status !== "GREEN" && it.status !== "UNKNOWN");
  const canApprove = data && !completed && !data.has_missing_inputs && (overall === "GREEN" || notes.trim().length > 0);

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="reconciliation-workspace">
      <Header data={data} onRefresh={load}
              periodStart={periodStart}
              onPeriodStartChange={setPeriodStart} />

      {loading ? (
        <div className="py-16 text-center text-slate-500" data-testid="reconciliation-loading">
          Loading reconciliation…
        </div>
      ) : data?.has_missing_inputs && data.items.every((it) => it.status === "UNKNOWN") ? (
        <MissingInputsPanel data={data} nav={nav} />
      ) : (
        <>
          {/* Exception-based summary banner + one-click Approve */}
          <SummaryBanner
            data={data}
            exceptions={exceptions}
            completed={completed}
            canApprove={canApprove}
            completing={completing}
            onApprove={complete}
          />

          {/* Threshold change notice · shown only when applicable */}
          {data.threshold_change_notice ? (
            <ThresholdChangeNotice notice={data.threshold_change_notice} />
          ) : null}

          {/* Sprint 10 · 2026-08-06 · Deliveries summary panel — shows
              what fed Book Inventory (approved) and what was excluded
              (pending / exception). Non-blocking; visibility only. */}
          <ReconciliationDeliveriesSummary
            periodStart={periodStart}
            onOpenSource={(code) => setSourceDrawer({ product_code: code })}
          />

          {/* Sprint 10 · 2026-08-12 · Today's Inventory Movements panel —
              editable RTS inputs per product plus read-only deliveries
              and throughput totals so the operator can sanity-check the
              math that feeds the adjusted variance. */}
          <InventoryMovementsPanel
            data={data}
            saving={saving}
            disabled={completed}
            onSaveRts={saveRts}
          />

          {/* Compact management report */}
          <ReconciliationReport
            items={data.items}
            thresholds={data.thresholds}
            investigating={investigating}
            onInvestigate={(code) => setInvestigating((prev) => ({ ...prev, [code]: !prev[code] }))}
            overrideEditor={overrideEditor}
            setOverrideEditor={setOverrideEditor}
            onSaveOverride={saveOverride}
            onRevertToAuto={revertToAuto}
            onOpenSource={(code) => setSourceDrawer({ product_code: code })}
            onSaveRts={saveRts}
            saving={saving}
            disabled={completed}
          />

          {/* Investigation Controls · Sprint 10 · WORKSTREAM 2 · 2026-08-13.
              Single-source-of-truth panel — same engine, same data path
              as the printed Audit Snapshot. Shows the four authoritative
              controls per product plus the day's disposition. */}
          <InvestigationControlsPanel data={data} />

          {/* Notes + approval — surfaces only when exceptions exist or after approval */}
          {(exceptions.length > 0 || completed) && (
            <ReviewNotesPanel
              data={data}
              notes={notes}
              setNotes={setNotes}
              completed={completed}
            />
          )}
        </>
      )}

      {/* Workflow footer */}
      <div className="mt-8 flex items-center justify-between text-sm">
        <button
          onClick={() => nav("/start-the-day/tank-inventory")}
          className="inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900"
          data-testid="reconciliation-back"
        >
          <ArrowLeft size={14} /> Tank Inventory
        </button>
        <button
          onClick={() => nav("/brief")}
          className={`inline-flex items-center gap-1.5 font-medium ${completed ? "text-slate-900 hover:text-black" : "text-slate-400 cursor-not-allowed"}`}
          disabled={!completed}
          data-testid="reconciliation-next"
        >
          Morning Brief <ArrowRight size={14} />
        </button>
      </div>

      {/* View Source drawer · Sprint 10 · 2026-08-06 */}
      {sourceDrawer && (
        <DeliveriesSourceDrawer
          productCode={sourceDrawer.product_code}
          periodStart={periodStart}
          onClose={() => setSourceDrawer(null)}
        />
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
function Header({ data, onRefresh, periodStart, onPeriodStartChange }) {
  return (
    <header className="mb-6" data-testid="reconciliation-header">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        Start the Day · Step 4
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold text-slate-900">Daily Inventory Review</h1>
          <p className="mt-1 text-sm text-slate-600">
            An exception-based report. The system computes book vs physical, gain/loss, and variance for every product — you decide whether it's acceptable.
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="text-xs inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900"
          data-testid="reconciliation-refresh"
        >
          <ArrowClockwise size={12} /> Refresh
        </button>
      </div>
      {/* Sprint 10 · 2026-08-10 · Operating-period range — supports Monday
          reconciling Fri + Sat + Sun in one pass. */}
      <div className="mt-4 flex flex-wrap items-end gap-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-600">
            Operating period from
          </label>
          <input
            type="date"
            value={periodStart || ""}
            onChange={(e) => onPeriodStartChange(e.target.value || null)}
            data-testid="reconciliation-period-start"
            className="mt-0.5 rounded border border-slate-300 bg-white px-2 py-1 text-sm font-mono focus:outline-none focus:border-slate-500"
          />
        </div>
        <div className="text-xs text-slate-600">
          → <span className="font-mono font-semibold">{data?.brief_date || "today"}</span> (as of today)
        </div>
        <div className="text-[11px] italic text-slate-500 flex-1">
          Default assumes weekends closed (Monday defaults to Friday). Adjust the "from" date to cover holidays or extended closures.
        </div>
      </div>
    </header>
  );
}


// ---------------------------------------------------------------------------
// Summary banner · exception-based, one-click Approve when clean.
// ---------------------------------------------------------------------------
function SummaryBanner({ data, exceptions, completed, canApprove, completing, onApprove }) {
  const meta = STATUS_META[data.overall_status] || STATUS_META.UNKNOWN;
  const t = TONE_CLASSES[meta.tone];
  const Icon = meta.icon;

  const headline = completed
    ? "Reconciliation Approved"
    : exceptions.length === 0
      ? "All products within tolerance — ready to approve"
      : `${exceptions.length} product${exceptions.length === 1 ? "" : "s"} outside tolerance — review required`;

  const subtext = completed
    ? `Approved by ${data.completion.acknowledged_by_name} · ${new Date(data.completion.acknowledged_at).toLocaleString()}`
    : `Variance thresholds — Pass ≤ ${data.thresholds.green_max_pct}% · Review > ${data.thresholds.green_max_pct}% · Escalate > ${data.thresholds.yellow_max_pct}%`;

  return (
    <div
      className={`mb-5 rounded-lg border ${t.card} p-5 flex flex-wrap items-center gap-4`}
      data-testid="reconciliation-summary-banner"
    >
      <div className={`rounded-md p-2.5 text-white ${t.bar}`}>
        <Icon size={26} weight="fill" />
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
          Daily Inventory Review
        </div>
        <div className="text-lg font-semibold text-slate-900" data-testid="reconciliation-summary-headline">
          {headline}
        </div>
        <p className="text-xs text-slate-600 mt-0.5" data-testid="reconciliation-summary-subtext">
          {subtext}
        </p>
      </div>
      {!completed && (
        <button
          onClick={onApprove}
          disabled={!canApprove || completing}
          data-testid="reconciliation-approve-btn"
          className="rounded-md bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed"
          title={
            canApprove
              ? (exceptions.length === 0 ? "One-click approve — all within tolerance" : "Approve with your review notes")
              : "Add investigation notes below before approving"
          }
        >
          <CheckCircle size={14} weight="bold" className="inline-block mr-1.5 -mt-0.5" />
          {completing ? "Approving…" : "Approve Reconciliation"}
        </button>
      )}
      {completed && (
        <div className="text-right text-xs text-emerald-800" data-testid="reconciliation-approved-stamp">
          <div className="font-semibold flex items-center gap-1">
            <CheckCircle size={12} weight="fill" /> Approved
          </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Today's Inventory Movements panel · Sprint 10 · 2026-08-12
// Shows the three inputs that feed Book Inventory (deliveries + RTS −
// throughput). RTS is editable inline per product; deliveries and
// throughput are read-only rollups from the authoritative sources.
// ---------------------------------------------------------------------------
function InventoryMovementsPanel({ data, saving, disabled, onSaveRts }) {
  const mv = data?.movements;
  const items = data?.items || [];
  if (!mv) return null;
  return (
    <section
      className="mt-4 rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid="reconciliation-movements-panel"
    >
      <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-700">
          Today's Inventory Movements
        </div>
        <div className="text-[11px] text-slate-500 italic">
          Adjusted Book = Book + RTS
        </div>
      </header>
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-4 py-2">Product</th>
            <th className="px-3 py-2 text-right">Supplier Deliveries</th>
            <th className="px-3 py-2 text-right">RTS (Return to Storage)</th>
            <th className="px-3 py-2 text-right">Throughput</th>
            <th className="px-3 py-2 text-right">Net Movement</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <MovementsRow
              key={it.product_code}
              item={it}
              disabled={disabled || saving}
              onSaveRts={onSaveRts}
            />
          ))}
          <tr className="border-t border-slate-200 bg-slate-50 font-semibold"
              data-testid="reconciliation-movements-totals">
            <td className="px-4 py-2 text-slate-700">Total</td>
            <td className="px-3 py-2 text-right font-mono text-slate-800">{fmtLitres(mv.deliveries_total)}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-800">{fmtLitres(mv.rts_total)}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-800">{fmtLitres(mv.throughput_total)}</td>
            <td className="px-3 py-2 text-right font-mono text-slate-900">{fmtGainLoss(mv.net_total)}</td>
          </tr>
        </tbody>
      </table>
    </section>
  );
}

function MovementsRow({ item, disabled, onSaveRts }) {
  const [litres, setLitres] = useState(item.rts ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setLitres(item.rts ?? ""); }, [item.rts]);

  const commit = async () => {
    const next = String(litres ?? "").trim();
    const cur  = item.rts == null ? "" : String(item.rts);
    if (next === cur) return;
    setSaving(true);
    try { await onSaveRts(item.product_code, next, null); } finally { setSaving(false); }
  };

  return (
    <tr className="border-b border-slate-100"
        data-testid={`reconciliation-movements-row-${item.product_code}`}>
      <td className="px-4 py-2 font-medium text-slate-800">{item.product_label}</td>
      <td className="px-3 py-2 text-right font-mono text-slate-700">
        {fmtLitres(item.deliveries)}
      </td>
      <td className="px-3 py-2 text-right">
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.1"
          value={litres}
          onChange={(e) => setLitres(e.target.value)}
          onBlur={commit}
          disabled={disabled || saving}
          data-testid={`reconciliation-movements-rts-${item.product_code}`}
          className="w-28 rounded border border-slate-300 bg-white px-2 py-1 text-xs font-mono text-right disabled:bg-slate-100"
          placeholder="0"
        />
      </td>
      <td className="px-3 py-2 text-right font-mono text-slate-700">
        {fmtLitres(item.throughput_yesterday)}
      </td>
      <td className="px-3 py-2 text-right font-mono text-slate-800">
        {(() => {
          const d = Number(item.deliveries || 0);
          const r = Number(item.rts || 0);
          const t = Number(item.throughput_yesterday || 0);
          return fmtGainLoss(String(d + r - t));
        })()}
      </td>
    </tr>
  );
}


// ---------------------------------------------------------------------------
// Compact management report table
// ---------------------------------------------------------------------------
function ReconciliationReport({
  items, thresholds, investigating, onInvestigate,
  overrideEditor, setOverrideEditor,
  onSaveOverride, onRevertToAuto, onOpenSource, onSaveRts,
  saving, disabled,
}) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid="reconciliation-report"
    >
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-600">
          <tr>
            <th className="px-4 py-2">Product</th>
            <th className="px-3 py-2 text-right">Book</th>
            <th className="px-3 py-2 text-right">Physical</th>
            <th className="px-3 py-2 text-right">Diff (L)</th>
            <th className="px-3 py-2 text-right">Var (%)</th>
            <th className="px-3 py-2 text-right">Tolerance</th>
            <th className="px-3 py-2 text-center">Status</th>
            <th className="px-3 py-2 text-right w-32"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <ReportRow
              key={it.product_code}
              item={it}
              thresholds={thresholds}
              open={!!investigating[it.product_code]}
              onToggle={() => onInvestigate(it.product_code)}
              overrideEditor={overrideEditor?.product_code === it.product_code ? overrideEditor : null}
              openOverrideEditor={(v) => setOverrideEditor({
                product_code: it.product_code,
                value: v ?? (it.deliveries || it.deliveries_auto || ""),
                reason: "",
              })}
              closeOverrideEditor={() => setOverrideEditor(null)}
              updateOverride={(patch) => setOverrideEditor((prev) => ({ ...prev, ...patch }))}
              onSaveOverride={onSaveOverride}
              onRevertToAuto={onRevertToAuto}
              onOpenSource={() => onOpenSource(it.product_code)}
              onSaveRts={onSaveRts}
              saving={saving}
              disabled={disabled}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportRow({ item, thresholds, open, onToggle,
                    overrideEditor, openOverrideEditor, closeOverrideEditor,
                    updateOverride, onSaveOverride, onRevertToAuto, onOpenSource,
                    onSaveRts, saving, disabled }) {
  const meta = STATUS_META[item.status] || STATUS_META.UNKNOWN;
  const t = TONE_CLASSES[meta.tone];
  const exception = item.status === "YELLOW" || item.status === "RED";
  const missing = item.missing_inputs?.length > 0;
  const rtsL = item.rts != null ? Number(item.rts) : 0;
  const hasRts = rtsL > 0;
  const diffRawNum = item.diff_raw != null ? Number(item.diff_raw) : null;
  const diffAdjNum = item.diff_adjusted != null ? Number(item.diff_adjusted) : null;
  const bookRawStr = item.book_inventory_raw ?? item.book_inventory;

  return (
    <>
      <tr
        className={`border-b border-slate-100 ${exception ? "bg-amber-50/40" : ""}`}
        data-testid={`reconciliation-row-${item.product_code}`}
      >
        <td className="px-4 py-3 font-semibold text-slate-900">{item.product_label}</td>
        <td className="px-3 py-3 text-right font-mono text-slate-800"
            data-testid={`reconciliation-book-${item.product_code}`}>
          {fmtLitres(bookRawStr)}
        </td>
        <td className="px-3 py-3 text-right font-mono text-slate-800">{fmtLitres(item.physical_inventory)}</td>
        <td className={`px-3 py-3 text-right font-mono ${exception ? "font-semibold text-amber-900" : "text-slate-700"}`}
            data-testid={`reconciliation-diff-${item.product_code}`}>
          {fmtGainLoss(item.diff_raw)}
        </td>
        <td className={`px-3 py-3 text-right font-mono ${exception ? "font-semibold text-amber-900" : "text-slate-700"}`}
            data-testid={`reconciliation-variance-${item.product_code}`}>
          {fmtPct(item.variance_pct)}
        </td>
        <td className="px-3 py-3 text-right text-xs text-slate-500 font-mono">≤ {thresholds.green_max_pct}%</td>
        <td className="px-3 py-3 text-center">
          <span
            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${t.pill}`}
            data-testid={`reconciliation-status-${item.product_code}`}
          >
            {item.status === "GREEN" ? "Pass" : item.status === "YELLOW" ? "Review" : item.status === "RED" ? "Escalate" : meta.label}
          </span>
        </td>
        <td className="px-3 py-3 text-right">
          {(exception || open) ? (
            <button
              onClick={onToggle}
              data-testid={`reconciliation-investigate-${item.product_code}`}
              className="text-xs font-medium rounded border border-amber-300 bg-white px-2.5 py-1 text-amber-900 hover:bg-amber-50"
            >
              {open ? "Hide" : "Investigate Variance"}
            </button>
          ) : (
            <span className="text-xs text-slate-400">—</span>
          )}
        </td>
      </tr>

      {/* RTS breakdown row · Sprint 10 · 2026-08-12 — shown whenever
          the operator has entered any RTS for this product. Makes the
          adjusted math transparent without opening the investigate
          panel. */}
      {hasRts && diffRawNum !== null && diffAdjNum !== null ? (
        <tr className="border-b border-slate-100 bg-slate-50/60"
            data-testid={`reconciliation-rts-breakdown-${item.product_code}`}>
          <td className="px-4 py-1.5 text-[11px] text-slate-500 italic">RTS adjustment</td>
          <td colSpan={7} className="px-3 py-1.5 text-[11px] font-mono text-slate-700">
            Raw <span className={diffRawNum > 0 ? "text-slate-800" : "text-slate-700"}>{fmtGainLoss(item.diff_raw)}</span>
            <span className="mx-2 text-slate-400">·</span>
            RTS <span className="text-slate-800">{fmtLitres(item.rts)}</span>
            <span className="mx-2 text-slate-400">·</span>
            Adjusted <span className={`font-semibold ${item.status === "GREEN" ? "text-emerald-700" : "text-amber-900"}`}>
              {fmtGainLoss(item.diff_adjusted)}
            </span>
            <span className="mx-2 text-slate-400">·</span>
            Adjusted Book <span className="text-slate-800">{fmtLitres(item.book_inventory_adjusted)}</span>
          </td>
        </tr>
      ) : null}

      {open && (
        <tr className="border-b border-slate-100 bg-slate-50/60"
            data-testid={`reconciliation-details-${item.product_code}`}>
          <td colSpan={8} className="px-6 py-4">
            {missing && (
              <div className="mb-3 text-xs text-amber-800" data-testid={`reconciliation-missing-${item.product_code}`}>
                Missing inputs: {item.missing_inputs.join(", ").replace(/_/g, " ")}.
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-2 text-xs">
              <MiniCell label="Prior physical"       value={fmtLitres(item.prior_physical)} />
              <MiniCell label="Yesterday's throughput" value={fmtLitres(item.throughput_yesterday)} />
              <MiniCell
                label="Deliveries today"
                value={fmtLitres(item.deliveries)}
                badge={<DeliverySourceBadge item={item} />}
              />
              <MiniCell label="RTS today"            value={hasRts ? fmtLitres(item.rts) : "—"} />
              <MiniCell label="Adjusted Gain / Loss" value={fmtGainLoss(item.diff_adjusted)} strong />
            </div>

            {/* Inline RTS editor · always available inside the investigate
                panel so operators can record returns without leaving the
                row. */}
            <RtsInlineEditor
              productCode={item.product_code}
              value={item.rts}
              note={item.rts_note}
              enteredBy={item.rts_entered_by}
              enteredAt={item.rts_entered_at}
              disabled={disabled || saving}
              onSave={(v, n) => onSaveRts(item.product_code, v, n)}
            />

            {/* Delivery provenance + override controls · Sprint 10 · 2026-08-06 */}
            <div className="mt-4 rounded-md border border-slate-200 bg-white px-3 py-2.5"
                 data-testid={`reconciliation-delivery-panel-${item.product_code}`}>
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-xs font-semibold text-slate-700">Deliveries source</div>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={onOpenSource}
                  data-testid={`reconciliation-view-source-${item.product_code}`}
                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded border border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
                >
                  <ListMagnifyingGlass size={13} weight="bold" />
                  View Source
                </button>
                {item.deliveries_source === "override" ? (
                  <button
                    type="button"
                    onClick={() => onRevertToAuto(item.product_code)}
                    disabled={saving || disabled}
                    data-testid={`reconciliation-revert-auto-${item.product_code}`}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <ArrowCounterClockwise size={13} weight="bold" />
                    Revert to auto
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => openOverrideEditor(item.deliveries || item.deliveries_auto || "")}
                  disabled={disabled}
                  data-testid={`reconciliation-override-btn-${item.product_code}`}
                  className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                >
                  <PencilSimple size={13} weight="bold" />
                  Override
                </button>
              </div>
              {item.deliveries_source === "override" && item.deliveries_override ? (
                <div className="mt-1.5 text-[11px] text-amber-900" data-testid={`reconciliation-override-audit-${item.product_code}`}>
                  Overridden by <b>{item.deliveries_override.by || "unknown"}</b>
                  {item.deliveries_override.at ? ` · ${new Date(item.deliveries_override.at).toLocaleString()}` : ""}
                  {item.deliveries_override.auto_at_time
                    ? <> · auto was <span className="font-mono">{fmtLitres(item.deliveries_override.auto_at_time)}</span></>
                    : null}
                  <div className="italic text-amber-800">"{item.deliveries_override.reason}"</div>
                </div>
              ) : null}
              {overrideEditor ? (
                <OverrideEditor
                  item={item}
                  value={overrideEditor.value}
                  reason={overrideEditor.reason}
                  onChange={updateOverride}
                  onCancel={closeOverrideEditor}
                  onSave={() => onSaveOverride(item.product_code, overrideEditor.value, overrideEditor.reason)}
                  saving={saving}
                />
              ) : null}
              <div className="mt-2 text-[11px] text-slate-500 italic">
                Book = Prior physical + Deliveries − Yesterday's throughput · Adjusted Book = Book + RTS
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Inline RTS input · Sprint 10 · 2026-08-12.
// Auto-saves on blur (matches the pattern used by other reconciliation
// controls). Empty submit clears the entry for this product.
function RtsInlineEditor({ productCode, value, note, enteredBy, enteredAt, disabled, onSave }) {
  const [litres, setLitres] = useState(value ?? "");
  const [noteVal, setNoteVal] = useState(note ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setLitres(value ?? ""); }, [value]);
  useEffect(() => { setNoteVal(note ?? ""); }, [note]);

  const commit = async () => {
    const nextLitres = String(litres ?? "").trim();
    const nextNote   = String(noteVal ?? "").trim();
    const curLitres  = value == null ? "" : String(value);
    const curNote    = note == null ? "" : String(note);
    if (nextLitres === curLitres && nextNote === curNote) return;
    setSaving(true);
    try { await onSave(nextLitres, nextNote); } finally { setSaving(false); }
  };

  return (
    <div className="mt-3 rounded-md border border-slate-200 bg-white px-3 py-2.5"
         data-testid={`reconciliation-rts-panel-${productCode}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-xs font-semibold text-slate-700">Return to Storage (RTS)</div>
        <label className="text-[11px] text-slate-500 flex items-center gap-1.5">
          Litres
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.1"
            value={litres}
            onChange={(e) => setLitres(e.target.value)}
            onBlur={commit}
            disabled={disabled || saving}
            data-testid={`reconciliation-rts-litres-${productCode}`}
            className="ml-1 w-28 rounded border border-slate-300 bg-white px-2 py-1 text-xs font-mono text-right disabled:bg-slate-100"
            placeholder="0"
          />
        </label>
        <label className="text-[11px] text-slate-500 flex-1 min-w-[220px] flex items-center gap-1.5">
          Note (optional)
          <input
            type="text"
            value={noteVal}
            onChange={(e) => setNoteVal(e.target.value)}
            onBlur={commit}
            disabled={disabled || saving}
            data-testid={`reconciliation-rts-note-${productCode}`}
            className="ml-1 flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-xs disabled:bg-slate-100"
            placeholder="Truck / driver / source"
          />
        </label>
      </div>
      {enteredBy || enteredAt ? (
        <div className="mt-1.5 text-[10px] text-slate-500 italic"
             data-testid={`reconciliation-rts-audit-${productCode}`}>
          Recorded by <b>{enteredBy || "unknown"}</b>
          {enteredAt ? ` · ${new Date(enteredAt).toLocaleString()}` : ""}
        </div>
      ) : null}
    </div>
  );
}

function DeliverySourceBadge({ item }) {
  if (item.deliveries_source === "override") {
    return (
      <span className="ml-1 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-900"
            data-testid={`reconciliation-badge-${item.product_code}`}>
        Override
      </span>
    );
  }
  if (item.deliveries_source === "auto") {
    const n = item.deliveries_receipt_count || 0;
    return (
      <span className="ml-1 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-800"
            data-testid={`reconciliation-badge-${item.product_code}`}>
        Auto · from {n} Approved Deliver{n === 1 ? "y" : "ies"}
      </span>
    );
  }
  return (
    <span className="ml-1 inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-500"
          data-testid={`reconciliation-badge-${item.product_code}`}>
      No deliveries
    </span>
  );
}

function OverrideEditor({ item, value, reason, onChange, onCancel, onSave, saving }) {
  const canSave = String(value ?? "").trim() !== "" && reason.trim().length >= 3;
  return (
    <div className="mt-3 rounded border border-amber-200 bg-amber-50/60 p-3 space-y-2"
         data-testid={`reconciliation-override-editor-${item.product_code}`}>
      <div className="text-xs text-amber-900 font-semibold">
        Override the auto-calculated Deliveries value
      </div>
      <div className="text-[11px] text-amber-800">
        Auto value from receipts:{" "}
        <span className="font-mono font-semibold">
          {item.deliveries_auto != null ? fmtLitres(item.deliveries_auto) : "—"}
        </span>
        {" · "}based on {item.deliveries_receipt_count || 0} approved deliver{(item.deliveries_receipt_count || 0) === 1 ? "y" : "ies"}.
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-slate-700 block mb-0.5">Override value (L)</label>
          <input
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={value ?? ""}
            onChange={(e) => onChange({ value: e.target.value })}
            className="w-40 rounded border border-slate-300 px-2 py-1 text-sm bg-white focus:border-slate-500 focus:outline-none"
            data-testid={`reconciliation-override-value-${item.product_code}`}
          />
        </div>
        <div className="flex-1 min-w-[240px]">
          <label className="text-xs text-slate-700 block mb-0.5">
            Reason <span className="text-rose-600">*required</span>
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => onChange({ reason: e.target.value })}
            placeholder="e.g., Late invoice not yet approved · confirmed with driver"
            maxLength={500}
            className="w-full rounded border border-slate-300 px-2 py-1 text-sm bg-white focus:border-slate-500 focus:outline-none"
            data-testid={`reconciliation-override-reason-${item.product_code}`}
          />
        </div>
        <button
          onClick={onSave}
          disabled={!canSave || saving}
          data-testid={`reconciliation-override-save-${item.product_code}`}
          className="text-xs font-medium px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save override"}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          data-testid={`reconciliation-override-cancel-${item.product_code}`}
          className="text-xs font-medium px-3 py-1.5 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function MiniCell({ label, value, strong, badge }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
        {badge}
      </div>
      <div className={`font-mono ${strong ? "font-semibold text-slate-900" : "text-slate-800"}`}>{value}</div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// View Source drawer · Sprint 10 · 2026-08-06.
// Lists the individual approved receipts contributing to the auto value.
// ---------------------------------------------------------------------------
function DeliveriesSourceDrawer({ productCode, periodStart, onClose }) {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = periodStart ? { period_start: periodStart } : {};
        const { data } = await api.get(`/reconciliation/today/deliveries/source/${productCode}`, { params });
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e });
      }
    })();
    return () => { cancelled = true; };
  }, [productCode, periodStart]);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-slate-900/40"
         data-testid="reconciliation-source-drawer" onClick={onClose}>
      <div className="w-full max-w-xl bg-white border-l border-slate-200 shadow-2xl overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Deliveries source</div>
            <div className="text-lg font-semibold text-slate-900">{productCode}</div>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-slate-100"
                  data-testid="reconciliation-source-drawer-close">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4">
          {state.loading && <div className="text-sm text-slate-500">Loading approved receipts…</div>}
          {state.error && <div className="text-sm text-rose-700">Could not load receipts.</div>}
          {state.data && (
            <>
              <div className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                Auto value · <span className="font-mono font-semibold">{fmtLitres(state.data.auto_value)}</span>
                {" · from "}<b>{state.data.count}</b> approved deliver{state.data.count === 1 ? "y" : "ies"}
                {" · business day "}<b>{state.data.delivery_date}</b>
              </div>
              {(state.data.receipts || []).length === 0 ? (
                <div className="text-sm text-slate-500 italic">
                  No approved receipts contribute to this product on {state.data.delivery_date}.
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="py-2 pr-2">Invoice</th>
                      <th className="py-2 pr-2">Supplier</th>
                      <th className="py-2 pr-2">Posted</th>
                      <th className="py-2 pr-2 text-right">Litres</th>
                      <th className="py-2 pl-2">State</th>
                      <th className="py-2 pl-2 w-8" aria-label="Open" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {state.data.receipts.map((r) => {
                      const invId = r.supplier_invoice_id;
                      const cellCls = invId ? "hover:bg-slate-50 cursor-pointer" : "";
                      const RowInner = (
                        <>
                          <td className="py-2 pr-2 font-mono">
                            {invId ? (
                              <Link
                                to={`/receiving/${invId}`}
                                onClick={onClose}
                                data-testid={`reconciliation-source-row-link-${r.id}`}
                                className="text-slate-900 underline decoration-slate-300 hover:decoration-slate-600"
                              >
                                {r.invoice_number || "—"}
                              </Link>
                            ) : (
                              r.invoice_number || "—"
                            )}
                          </td>
                          <td className="py-2 pr-2">{r.supplier || "—"}</td>
                          <td className="py-2 pr-2 text-slate-500">
                            {r.posted_at ? new Date(r.posted_at).toLocaleString() : "—"}
                          </td>
                          <td className="py-2 pr-2 text-right font-mono">{fmtLitres(r.litres)}</td>
                          <td className="py-2 pl-2 text-slate-600">
                            {(r.business_state || "").replace(/_/g, " ")}
                          </td>
                          <td className="py-2 pl-2 text-slate-400">
                            {invId ? <ArrowRight size={14} /> : null}
                          </td>
                        </>
                      );
                      return (
                        <tr
                          key={r.id}
                          data-testid={`reconciliation-source-row-${r.id}`}
                          className={cellCls}
                          onClick={invId ? () => { onClose(); nav(`/receiving/${invId}`); } : undefined}
                        >
                          {RowInner}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Deliveries summary panel · Sprint 10 · 2026-08-06 PO directive.
// Shows what fed Book Inventory (approved) + what was excluded
// (pending / exception). Non-blocking; visibility only.
// ---------------------------------------------------------------------------
function ReconciliationDeliveriesSummary({ periodStart, onOpenSource }) {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, data: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = periodStart ? { period_start: periodStart } : {};
        const { data } = await api.get("/reconciliation/deliveries/summary", { params });
        if (!cancelled) setState({ loading: false, data });
      } catch {
        if (!cancelled) setState({ loading: false, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [periodStart]);

  if (state.loading || !state.data) return null;
  const d = state.data;
  const anyExcluded = (d.pending.count + d.exception.count) > 0;

  return (
    <section
      data-testid="reconciliation-deliveries-summary"
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
    >
      <header className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">
          Deliveries Feeding Today's Book Inventory
        </div>
        <div className="text-[11px] text-slate-500">
          Business day <span className="font-mono">{d.delivery_date}</span>
        </div>
      </header>

      {/* Approved side */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">
            Included · Approved
          </span>
          <span className="text-xs text-slate-600">
            {d.approved.count} invoice{d.approved.count === 1 ? "" : "s"} · <span className="font-mono">{d.approved.total_litres} L</span> total
          </span>
        </div>
        {d.approved.count === 0 ? (
          <div className="text-xs italic text-slate-500">
            No approved deliveries on {d.delivery_date}. Book Inventory carries forward without any deliveries added.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {d.approved.by_product.map((p) => (
              <button
                key={p.product_code}
                type="button"
                onClick={() => onOpenSource(p.product_code)}
                data-testid={`reconciliation-summary-product-${p.product_code}`}
                className="text-left rounded border border-emerald-200 bg-emerald-50/50 px-3 py-2 hover:bg-emerald-100 transition"
              >
                <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">{p.product_label}</div>
                <div className="mt-0.5 font-mono text-sm font-semibold text-slate-900">
                  {p.litres === "0" ? "—" : `${Number(p.litres).toLocaleString()} L`}
                </div>
                <div className="text-[10px] text-emerald-700">
                  {p.count} approved · view source
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Not Included · pending & exception */}
      {anyExcluded ? (
        <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/40"
             data-testid="reconciliation-summary-not-included">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-700">
              Not Included in Book Inventory
            </span>
            <span className="text-xs text-slate-500 italic">
              Excluded until approved · Receiving is the single source of truth
            </span>
          </div>
          {d.pending.count > 0 ? (
            <ExcludedInvoiceRow
              tone="slate" label="Pending Supplier Invoices"
              invoices={d.pending.invoices} totalLitres={d.pending.total_litres}
              testid="reconciliation-summary-pending"
              actionLabel="Review in Receiving"
              onAction={() => nav("/receiving")}
            />
          ) : null}
          {d.exception.count > 0 ? (
            <ExcludedInvoiceRow
              tone="amber" label="Exception Receipts"
              invoices={d.exception.invoices} totalLitres={d.exception.total_litres}
              testid="reconciliation-summary-exception"
              actionLabel="Open Exceptions"
              onAction={() => nav("/receiving/exceptions")}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function ExcludedInvoiceRow({ tone, label, invoices, totalLitres, actionLabel, onAction, testid }) {
  const border = tone === "amber" ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-white";
  const chip   = tone === "amber" ? "text-amber-900 bg-amber-100 border-amber-300"
                                   : "text-slate-700 bg-slate-100 border-slate-300";
  return (
    <div className={`mt-1.5 rounded border ${border} px-3 py-2`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-xs font-semibold text-slate-800">{label}</div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-600">
            {invoices.length} · <span className="font-mono">{totalLitres} L</span>
          </span>
          <button
            type="button"
            onClick={onAction}
            className="text-[11px] font-medium rounded border border-slate-300 bg-white px-2 py-0.5 hover:bg-slate-50"
          >
            {actionLabel}
          </button>
        </div>
      </div>
      <ul className="space-y-1">
        {invoices.map((inv) => (
          <li key={inv.id} className="flex items-center gap-2 text-[11px]"
              data-testid={`${testid}-inv-${inv.id}`}>
            <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${chip}`}>
              {(inv.business_state || "").replace(/_/g, " ")}
            </span>
            {inv.id ? (
              <Link
                to={`/receiving/${inv.id}`}
                data-testid={`${testid}-inv-link-${inv.id}`}
                className="font-mono text-slate-800 underline decoration-slate-300 hover:decoration-slate-600"
                title="Open in Receiving"
              >
                {inv.invoice_number || "—"}
              </Link>
            ) : (
              <span className="font-mono text-slate-700">{inv.invoice_number || "—"}</span>
            )}
            <span className="text-slate-500">· {inv.supplier || "—"}</span>
            <span className="ml-auto font-mono text-slate-600">{inv.litres} L</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Review notes panel · surfaces only when exceptions exist or after approval.
// ---------------------------------------------------------------------------
function ReviewNotesPanel({ data, notes, setNotes, completed }) {
  const overall = data.overall_status;

  if (completed) {
    return (
      <div
        className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4"
        data-testid="reconciliation-completed-banner"
      >
        <div className="flex items-start gap-3">
          <CheckCircle size={22} className="text-emerald-700" weight="fill" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-emerald-900">
              Approved by {data.completion.acknowledged_by_name}
            </div>
            <p className="text-xs text-emerald-800 mt-0.5">
              Overall status locked as <b>{data.completion.overall_status}</b> at{" "}
              {new Date(data.completion.acknowledged_at).toLocaleString()}.
              {data.completion.investigation_notes ? (
                <>
                  <br />Investigation notes: <span className="italic">"{data.completion.investigation_notes}"</span>
                </>
              ) : null}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-5 rounded-lg border border-slate-300 bg-white p-5"
      data-testid="reconciliation-review-panel"
    >
      <div className="font-display text-base font-semibold text-slate-900">Investigation Notes</div>
      <p className="text-xs text-slate-600 mt-1">
        {overall === "YELLOW"
          ? "One or more products are outside tolerance. Describe your review before approving."
          : overall === "RED"
          ? "One or more products exceed the escalation threshold. Document the cause + corrective action before approving."
          : "Optional — add a note if anything unusual happened today."}
      </p>
      <textarea
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Describe cause · corrective action · sign-off"
        className="mt-3 w-full rounded border border-slate-300 px-3 py-2 text-sm bg-white focus:border-slate-500 focus:outline-none"
        data-testid="reconciliation-notes-input"
      />
      <p className="mt-2 text-[11px] text-slate-500 italic">
        Financial control · your approval is recorded to the audit log.
      </p>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Threshold change notice · PO 2026-08-01
// "Show it only when it affects today's reconciliation."
// ---------------------------------------------------------------------------
const NOTICE_KEY_LABEL = {
  green_max_pct:  "Green (within tolerance) variance threshold",
  yellow_max_pct: "Yellow (investigate) variance threshold",
};

function ThresholdChangeNotice({ notice }) {
  if (!notice || !notice.changes?.length) return null;
  return (
    <div
      className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
      data-testid="reconciliation-threshold-change-notice"
    >
      <div className="flex items-start gap-3">
        <Warning size={18} className="text-amber-700 shrink-0 mt-0.5" weight="fill" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-amber-900">
            Reconciliation thresholds changed
          </div>
          <p className="text-xs text-amber-900/90 mt-0.5">
            Today's reconciliation is being evaluated using updated thresholds.
          </p>
          <ul className="mt-2 space-y-1.5" data-testid="reconciliation-threshold-change-list">
            {notice.changes.map((c, i) => (
              <li key={c.id || i} className="text-xs text-amber-900" data-testid={`reconciliation-threshold-change-${c.key}`}>
                <b>{NOTICE_KEY_LABEL[c.key] || c.key}</b>
                <div className="mt-0.5 grid grid-cols-3 gap-x-4 text-[11px]">
                  <span><span className="text-amber-800/70">Previous:</span> {c.previous_value ?? "—"}%</span>
                  <span><span className="text-amber-800/70">Current:</span>  <b>{c.new_value ?? "—"}%</b></span>
                  <span className="text-amber-800/80">
                    by {c.updated_by || "unknown"}
                    {c.updated_at ? ` · ${new Date(c.updated_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Missing-inputs panel (all UNKNOWN)
// ---------------------------------------------------------------------------
function MissingInputsPanel({ data, nav }) {
  // Aggregate which of the three inputs is missing, and for which products.
  const buckets = {
    prior_physical:       [],
    throughput_yesterday: [],
    physical_today:       [],
  };
  for (const item of (data?.items || [])) {
    for (const input of (item.missing_inputs || [])) {
      if (buckets[input]) buckets[input].push(item.product_code);
    }
  }
  const rows = [
    {
      key:    "prior_physical",
      title:  "Yesterday's closing Tank Inventory",
      hint:   "Reconciliation compares today's physical against yesterday's closing. Without a Tank Inventory recorded for yesterday, there is no baseline to project against.",
      fix:    "Ask an administrator to backfill yesterday's Tank Inventory (the platform does not offer an operator UI for prior dates). Once recorded, refresh this page.",
      products: buckets.prior_physical,
      cta:    null,   // no operator UI for past-date tank inventory
    },
    {
      key:    "throughput_yesterday",
      title:  "Today's Daily Readings",
      hint:   "Reconciliation needs today's readings to know how much fuel actually moved since yesterday's close.",
      fix:    "Enter this morning's pump / meter totals for each cardlock and loading-rack lane.",
      products: buckets.throughput_yesterday,
      cta:    { label: "Complete Daily Readings →", to: "/start-the-day/daily-readings", testid: "reconciliation-goto-readings" },
    },
    {
      key:    "physical_today",
      title:  "Today's Tank Inventory",
      hint:   "Reconciliation needs this morning's physical stick reading to compare against the computed book position.",
      fix:    "Record today's stick reading for each active tank.",
      products: buckets.physical_today,
      cta:    { label: "Complete Tank Inventory →", to: "/start-the-day/tank-inventory", testid: "reconciliation-goto-inventory" },
    },
  ].filter((r) => r.products.length > 0);

  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50 p-5"
      data-testid="reconciliation-missing-panel"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-amber-600 p-2 text-white shrink-0">
          <Info size={22} weight="fill" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">
            Waiting on prior inputs
          </div>
          <div className="font-display text-lg font-semibold text-amber-900">
            Reconciliation cannot compute yet
          </div>
          <p className="mt-1 text-sm text-amber-900/90">
            Each product below is missing a specific input. Complete the ones you can from here; anything on a past date has to be backfilled by an administrator.
          </p>

          <ul className="mt-4 space-y-3">
            {rows.map((r) => (
              <li
                key={r.key}
                data-testid={`reconciliation-missing-${r.key}`}
                className="rounded-md border border-amber-300 bg-white p-3"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{r.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      Missing for: {r.products.map((p) => p.replace(/_/g, " ")).join(", ")}
                    </div>
                    <p className="mt-2 text-xs text-slate-700">{r.hint}</p>
                    <p className="mt-1 text-xs text-slate-500 italic">{r.fix}</p>
                  </div>
                  {r.cta ? (
                    <button
                      type="button"
                      onClick={() => nav(r.cta.to)}
                      data-testid={r.cta.testid}
                      className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded bg-amber-800 text-white hover:bg-amber-900"
                    >
                      {r.cta.label}
                    </button>
                  ) : (
                    <span
                      data-testid={`${r.key}-admin-required`}
                      className="shrink-0 rounded-full border border-slate-300 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold text-slate-600"
                    >
                      Administrator required
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// InvestigationControlsPanel · Sprint 10 · WORKSTREAM 2 · 2026-08-13
// ---------------------------------------------------------------------------
// Displays the single-source-of-truth investigation controls returned by
// the reconciliation endpoint (which delegates to routes/investigation_controls.py
// via routes/daily_package._load_investigation_controls). This panel and the
// Audit Snapshot page render THE SAME data — LAW-007 hard acceptance criterion.
// ---------------------------------------------------------------------------
function InvestigationControlsPanel({ data }) {
  const ic = data?.investigation_controls;
  const disposition = data?.disposition;
  if (!ic) return null;

  const PRODUCT_LABEL = {
    regular: "Regular Gasoline", premium: "Premium Gasoline",
    clear_diesel: "Clear Diesel", dyed_diesel: "Dyed Diesel",
  };
  const dispTone = disposition?.tone || (ic.any_triggered ? "amber"
                                        : ic.any_incomplete ? "slate" : "green");
  const dispCls = dispTone === "green"
    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
    : dispTone === "amber"
      ? "border-amber-300 bg-amber-50 text-amber-900"
      : dispTone === "red"
        ? "border-rose-300 bg-rose-50 text-rose-900"
        : "border-slate-300 bg-slate-100 text-slate-700";

  const dailyRows = Object.entries(ic.by_product || {});
  return (
    <section
      data-testid="reconciliation-investigation-controls"
      className="mt-6 rounded-lg border border-slate-300 bg-white overflow-hidden"
    >
      <header className="border-b border-slate-200 bg-slate-50 px-4 py-2.5 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Investigation Controls</div>
          <div className="text-[11px] italic text-slate-500 mt-0.5">{ic.policy}</div>
        </div>
        {disposition ? (
          <span
            data-testid={`recon-disposition-${disposition.code?.toLowerCase()}`}
            className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${dispCls}`}
          >
            {disposition.label}
          </span>
        ) : null}
      </header>

      {/* Daily percentage variance — per product with limit + result */}
      <div className="px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Daily Variance</div>
        <table className="w-full text-xs mt-1.5" data-testid="recon-daily-variance-table">
          <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-1 pr-2">Product</th>
              <th className="py-1 pr-2 text-right">Daily Variance</th>
              <th className="py-1 pr-2 text-right">Daily Limit</th>
              <th className="py-1 pr-2">Result</th>
            </tr>
          </thead>
          <tbody>
            {dailyRows.map(([code, block]) => {
              const dv = block.daily_variance || {};
              const pct = dv.variance_pct ? `${Number(dv.variance_pct).toFixed(2)}%` : "n/a";
              const lim = dv.threshold_pct ? `±${Number(dv.threshold_pct).toFixed(2)}%` : "—";
              return (
                <tr key={code} className="border-b border-slate-100" data-testid={`recon-dv-${code}`}>
                  <td className="py-1 pr-2">{PRODUCT_LABEL[code] || code}</td>
                  <td className="py-1 pr-2 text-right font-mono">{pct}</td>
                  <td className="py-1 pr-2 text-right font-mono text-slate-500">{lim}</td>
                  <td className="py-1 pr-2">
                    <span
                      className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold ${
                        dv.triggered
                          ? "border-amber-300 bg-amber-50 text-amber-900"
                          : "border-emerald-200 bg-emerald-50 text-emerald-800"
                      }`}
                      data-testid={`recon-dv-${code}-${dv.triggered ? "outside" : "within"}`}
                    >
                      {dv.triggered ? "Outside" : "Within"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Historical investigation controls — the multi-day rules */}
      <div className="border-t border-slate-200 px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
          Historical Investigation Controls
        </div>
        <div className="text-[10px] italic text-slate-500 mt-0.5">
          Multi-day sequences and monthly counts — evaluated across the operating calendar.
        </div>
        <table className="w-full text-xs mt-1.5" data-testid="recon-historical-controls-table">
          <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-1 pr-2">Product</th>
              <th className="py-1 pr-2 text-center">5 Consecutive Losses</th>
              <th className="py-1 pr-2 text-center">18 Loss/Gain Days</th>
              <th className="py-1 pr-2 text-center">3 Consec. Losses &gt; 200 L</th>
              <th className="py-1 pr-2 text-center">30-Day Cum. Shortage &gt; 5%</th>
            </tr>
          </thead>
          <tbody>
            {dailyRows.map(([code, block]) => (
              <tr key={code} className="border-b border-slate-100" data-testid={`recon-hc-${code}`}>
                <td className="py-1 pr-2">{PRODUCT_LABEL[code] || code}</td>
                <ControlCell code={code} ctlKey="five_consecutive_losses" ctl={block.five_consecutive_losses} />
                <ControlCell code={code} ctlKey="eighteen_loss_gain_days" ctl={block.eighteen_loss_gain_days} />
                <ControlCell code={code} ctlKey="three_consecutive_over_200" ctl={block.three_consecutive_over_200} />
                <ControlCell code={code} ctlKey="thirty_day_cumulative_shortage" ctl={block.thirty_day_cumulative_shortage} />
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-2 text-[10px] italic text-slate-500">
          These historical counters keep running regardless of a day's variance
          explanation. An operator note explains a variance — it does not reset
          the control.
        </div>
      </div>
    </section>
  );
}

function ControlCell({ code, ctlKey, ctl }) {
  const c = ctl || {};
  // Sprint 10 · Historical Inventory Controls · 2026-08-13.
  // Distinguish TRIGGERED / CLEAR / INCOMPLETE — never dress an
  // insufficient-history state as "Not triggered".
  const status = c.status || (c.triggered ? "TRIGGERED" : "CLEAR");
  const count = ctlKey === "eighteen_loss_gain_days"
    ? `${c.count ?? 0}/${c.threshold ?? "—"}`
    : ctlKey === "thirty_day_cumulative_shortage"
      ? (c.shortage_pct ? `${Number(c.shortage_pct).toFixed(2)}% / ${Number(c.threshold_pct || 5).toFixed(2)}%`
         : `${c.history_days_available ?? 0}/${c.history_days_required ?? 30} days`)
      : `${c.current_run ?? 0}/${c.threshold ?? "—"}`;
  const label = status === "TRIGGERED" ? "Triggered"
              : status === "INCOMPLETE" ? "Incomplete"
              : "Not triggered";
  const tone = status === "TRIGGERED"
    ? "border-amber-400 bg-amber-50 text-amber-900"
    : status === "INCOMPLETE"
      ? "border-slate-300 bg-slate-50 text-slate-600"
      : "border-emerald-200 bg-emerald-50 text-emerald-800";
  return (
    <td className="py-1 pr-2 text-center align-top">
      <div
        className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone}`}
        title={c.detail || ""}
        data-testid={`recon-hc-${code}-${ctlKey}-${status.toLowerCase()}`}
      >
        {label}
      </div>
      <div className="mt-0.5 font-mono text-[10px] text-slate-500">{count}</div>
      {status === "TRIGGERED" && (c.current_run_dates?.length || c.dates_in_month?.length) ? (
        <div className="mt-0.5 text-[9px] text-amber-800 max-w-[180px] mx-auto break-words">
          {(c.current_run_dates || c.dates_in_month || []).slice(-5).join(", ")}
        </div>
      ) : null}
    </td>
  );
}

// ---------------------------------------------------------------------------
// (Legacy ReviewCompletePanel removed 2026-08-04 — approval now happens on
// the summary banner; investigation notes moved to ReviewNotesPanel which
// only surfaces when exceptions are present.)
// ---------------------------------------------------------------------------

